import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {   Users, Plus, Trash2, MoreVertical,   ChevronUp, ChevronDown, RefreshCw, CheckCircle2,  Edit, Hash, Mail, Phone, Contact} from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import { getToken } from '../utils/auth';
import LoadingScreen from '../components/LoadingScreen';

const API_BASE = import.meta.env.VITE_API_BASE;

// User Avatar Component
function UserAvatar({ user }) {
  const [imageError, setImageError] = useState(false);

  // Get initials from full name or email
  const getInitials = () => {
    if (user.fullName || user.name) {
      const fullName = user.fullName || user.name;
      const nameParts = fullName.split(' ').filter(part => part.length > 0);
      if (nameParts.length >= 2) {
        return (nameParts[0][0] + nameParts[1][0]).toUpperCase();
      }
      return nameParts[0][0].toUpperCase();
    }
    return user.email[0].toUpperCase();
  };

  if (user.avatarUrl && !imageError) {
    return (
      <div className="w-8 h-8 rounded-full overflow-hidden ring-1 ring-purple-100/20">
        <img
          src={user.avatarUrl}
          alt={user.fullName || user.name || user.email}
          className="w-full h-full object-cover"
          onError={() => setImageError(true)}
        />
      </div>
    );
  }

  return (
    <div
      className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium text-white ring-1 ring-purple-100/20"
      style={{ backgroundColor: '#B356C6' }}
    >
      {getInitials()}
    </div>
  );
}

// No Level and Status mappings needed

export default function TeamManagement() {
  const { userData } = useUser();
  const navigate = useNavigate();

  // States
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [teams, setTeams] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const mountedRef = useRef(false);
  
  // For dropdown menu positioning
  const [actionMenuOpen, setActionMenuOpen] = useState(null);
  const [buttonPosition, setButtonPosition] = useState({ bottom: 0, left: 0 });
  
  // Modals
  const [showAddTeamModal, setShowAddTeamModal] = useState(false);
  const [showEditTeamModal, setShowEditTeamModal] = useState(false);
  const [showArchiveTeamModal, setShowArchiveTeamModal] = useState(false);
  const [showTeamMembersModal, setShowTeamMembersModal] = useState(false);
  const [isArchiveConfirmed, setIsArchiveConfirmed] = useState(false);
  
  // Mobile view
  const [showSortModal, setShowSortModal] = useState(false);
  const [tempSortConfig, setTempSortConfig] = useState({ key: 'memberCount', direction: 'desc' });
  
  // Show unassigned users modal
  const [showUnassignedUsersModal, setShowUnassignedUsersModal] = useState(false);
  const [selectedTeamForAssignment, setSelectedTeamForAssignment] = useState(null);
  const [showAssignTeamModal, setShowAssignTeamModal] = useState(false);
  
  // Selected item
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [addingUsersToTeam, setAddingUsersToTeam] = useState(false);
  const [removingUserFromTeam, setRemovingUserFromTeam] = useState(false);
  
  // Sort
  const [sortConfig, setSortConfig] = useState({ key: 'memberCount', direction: 'desc' });

  // Remove team
  const [removingTeam, setRemovingTeam] = useState(false);

  // Add a new team
  const [newTeam, setNewTeam] = useState({ name: '' });
  const [creatingTeam, setCreatingTeam] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);

  // Edit existing team
  const [editingTeam, setEditingTeam] = useState(false);
  const [editedTeam, setEditedTeam] = useState({ name: '' });

  // Set up edited team whenever selected team changes
  useEffect(() => {
    if (selectedTeam) {
      setEditedTeam({ name: selectedTeam.name });
    }
  }, [selectedTeam]);

  // Add a new ref and useEffect for handling clicks outside the action menu
  const actionMenuRef = useRef(null);

  // Update the click outside handler in your useEffect
  useEffect(() => {
    const handleClickOutside = (event) => {
      // If we have an open action menu and user clicks outside of any menu button or menu content
      if (actionMenuOpen !== null && 
          !event.target.closest('[data-menu-button]') && 
          !event.target.closest('[data-menu-content]')) {
        setActionMenuOpen(null);
      }
    };

    // Add global event listener
    document.addEventListener('mousedown', handleClickOutside);
    
    // Clean up
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [actionMenuOpen]); // Only re-add when actionMenuOpen changes

  // Sort icon component
  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <ChevronUp className="w-4 h-4 opacity-0 group-hover:opacity-100 text-[var(--text-primary)]" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ChevronUp className="w-4 h-4 text-[var(--text-primary)]" />
      : <ChevronDown className="w-4 h-4 text-[var(--text-primary)]" />;
  };
  
  // Handle sort
  const handleSort = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Sort data
    const sortData = (data) => {    return [...data].sort((a, b) => {      let comparison = 0;            switch (sortConfig.key) {        case 'name':          comparison = a.name.localeCompare(b.name);          break;        case 'memberCount':          comparison = a.memberCount - b.memberCount;          break;        case 'createdAt':          comparison = new Date(a.createdAt) - new Date(b.createdAt);          break;        default:          comparison = 0;      }            return sortConfig.direction === 'asc' ? comparison : -comparison;    });  };
  
  // Calculate position for dropdown menu
  const calculateMenuPosition = (rect) => {
    const leftPosition = Math.max(20, Math.min(rect.left - 150, window.innerWidth - 220)); // Ensure menu stays within viewport
    return {
      top: rect.bottom + 5,
      left: leftPosition
    };
  };

  // For unassigned users
  const [unassignedUsers, setUnassignedUsers] = useState([]);

  // Helper function to handle unauthorized responses
  const handleUnauthorized = () => {
    console.log('User unauthorized, redirecting to login');
    navigate('/login');
    return;
  };

  // Update the fetchUsers function to use the helper
  const fetchUsers = async (teamsData = null) => {
    try {
      const token = getToken();
      if (!token) {
        handleUnauthorized();
        return;
      }

      const response = await fetch(`${API_BASE}/company/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const users = await response.json();
        
        // Log user information for debugging
        console.log("All users:", users);
        
        // Filter out users without a team assignment
        const usersWithoutTeam = users.filter(user => !user.teamName);
        setUnassignedUsers(usersWithoutTeam);
        
        // Use the teamsData parameter if provided, otherwise use the teams state
        const teamsToUpdate = teamsData || teams;
        
        // Make sure we have teams before updating counts
        if (teamsToUpdate && teamsToUpdate.length > 0) {
          updateTeamMemberCounts(users, teamsToUpdate);
        } else {
          console.warn("No teams available to update counts");
        }
      } else if (response.status === 401) {
        handleUnauthorized();
      } else {
        console.error('Failed to fetch users');
      }
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  // Update teams with member counts based on teamName
  const updateTeamMemberCounts = (users, teamsToUpdate) => {
    if (!users || !Array.isArray(users) || users.length === 0 || !teamsToUpdate || teamsToUpdate.length === 0) {
      return;
    }
    
    try {
      // Create new team objects with counts
      const teamsWithCounts = teamsToUpdate.map(team => {
        // Find all users with matching team name (case insensitive)
        const teamUsers = users.filter(user => 
          user.teamName && user.teamName.toLowerCase() === team.name.toLowerCase()
        );
        
        // Return a new team object with the count
        return {
          ...team,
          memberCount: teamUsers.length
        };
      });
      
      console.log("Teams with counts:", teamsWithCounts.map(t => `${t.name}: ${t.memberCount}`));
      
      // Force a state update with the new array of team objects
      setTeams([...teamsWithCounts]);
    } catch (error) {
      console.error("Error updating team member counts:", error);
    }
  };

  // Update the useEffect to call fetchTeams and fetchUsers
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      if (userData) {
        if (!['Owner', 'Admin'].includes(userData.permissionsId)) {
          navigate('/home');
          return;
        }
        // Just call fetchTeams - it will handle fetchUsers internally now
        fetchTeams();
      } else {
        navigate('/login');
      }
    }
  }, [userData, navigate]);

  // We removed the duplicate users fetch effect that was causing extra API calls

  // Once we have API endpoints
  const fetchTeams = async () => {
    try {
      const token = getToken();
      if (!token) {
        handleUnauthorized();
        return;
      }

      setError('');
      setRefreshing(true);
      
      // Don't clear teams immediately to avoid flicker
      // setTeams([]);

      const response = await fetch(`${API_BASE}/company/teams`, {
        method: 'GET', // Explicitly set method
        headers: {
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache' // Prevent caching
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log("Fetched teams:", data);
        
        // Only update if we got data
        if (data && Array.isArray(data)) {
          // Set teams state first
          setTeams(data);
          
          // Fetch users after teams to ensure counts are updated
          // Pass the teams data directly to fetchUsers so we don't depend on state update timing
          await fetchUsers(data);
        } else {
          console.error("Received invalid teams data:", data);
          setError('Received invalid data format from server');
        }
      } else if (response.status === 401) {
        handleUnauthorized();
      } else {
        throw new Error(`Failed to fetch teams. Status: ${response.status}`);
      }
    } catch (err) {
      console.error("Error fetching teams:", err);
      setError('Failed to fetch teams. Please try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Add a new team
  const handleAddTeam = async (e) => {
    e.preventDefault();
    
    if (creatingTeam) return;
    if (!newTeam.name.trim()) {
      setError('Team name is required');
      return;
    }

    setCreatingTeam(true);
    setError('');

    try {
      const token = getToken();
      if (!token) {
        handleUnauthorized();
        return;
      }

      // Format team name with proper capitalization
      const formattedName = newTeam.name.trim().charAt(0).toUpperCase() + newTeam.name.trim().slice(1);

      // Step 1: Create the team first
      const response = await fetch(`${API_BASE}/account/teams`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache' // Prevent caching
        },
        body: JSON.stringify({
          name: formattedName
        })
      });

      // Handle empty/invalid response body
      let createdTeam;
      if (response.ok) {
        try {
          if (response.headers.get('content-length') === '0') {
            console.log("Empty response body, but team creation succeeded");
            // Create a mock team with a temporary ID
            createdTeam = { 
              id: `temp_${Date.now()}`, 
              name: formattedName,
              memberCount: 0
            };
          } else {
            createdTeam = await response.json();
            // Ensure it has required properties
            if (!createdTeam.memberCount) {
              createdTeam.memberCount = 0;
            }
          }
          
          console.log("Created team:", createdTeam);
          
          // IMPORTANT: Add the new team directly to the UI state
          setTeams(prevTeams => [...prevTeams, createdTeam]);
          
          // Step 2: If team creation was successful and users are selected, assign them
          if (selectedUsers.length > 0) {
            let assignedCount = 0;
            // Assign each user one at a time
            for (const userId of selectedUsers) {
              try {
                const assignResponse = await fetch(`${API_BASE}/company/users/${userId}/team`, {
                  method: 'PUT',
                  headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Cache-Control': 'no-cache' // Prevent caching
                  },
                  body: JSON.stringify({ 
                    teamId: createdTeam.id,
                    teamName: formattedName
                  })
                });
                
                if (assignResponse.ok) {
                  assignedCount++;
                  
                  // Update the member count in our UI state incrementally
                  setTeams(prevTeams => 
                    prevTeams.map(team => 
                      team.id === createdTeam.id 
                        ? { ...team, memberCount: team.memberCount + 1 }
                        : team
                    )
                  );
                }
              } catch (userError) {
                console.error(`Error assigning user ${userId} to team:`, userError);
              }
            }
            
            // Update unassigned users list
            setUnassignedUsers(prev => prev.filter(user => !selectedUsers.includes(user.id)));
          }
          
          // Step 3: Reset form and close modal
          setNewTeam({ name: '' });
          setSelectedUsers([]);
          setShowAddTeamModal(false);
          
          // Step 5: Force a background refresh to ensure data consistency
          setTimeout(() => {
            fetchTeams();
          }, 1000);
          
        } catch (jsonError) {
          console.error("Error handling team creation response:", jsonError);
          throw new Error("Failed to process team creation response");
        }
      } else if (response.status === 401) {
        handleUnauthorized();
        return;
      } else {
        // Try to parse error message from response
        try {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to create team');
        } catch (jsonError) {
          throw new Error('Failed to create team. Server returned an error.');
        }
      }
    } catch (err) {
      console.error("Team creation error:", err);
      setError(err.message || 'Failed to create team');
    } finally {
      // Ensure spinner state is fully reset
      setTimeout(() => {
        setCreatingTeam(false);
      }, 100);
    }
  };

  // Edit existing team
  const handleEditTeam = async (e) => {
    e.preventDefault();
    
    if (editingTeam || !selectedTeam) return;
    if (!editedTeam.name.trim()) {
      setError('Team name is required');
      return;
    }

    setEditingTeam(true);
    setError('');

    try {
      const token = getToken();
      if (!token) {
        handleUnauthorized();
        return;
      }

      // Format team name with proper capitalization
      const formattedName = editedTeam.name.trim().charAt(0).toUpperCase() + editedTeam.name.trim().slice(1);

      const response = await fetch(`${API_BASE}/company/teams/${selectedTeam.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: formattedName })
      });

      if (response.ok) {
        // Close modal
        setShowEditTeamModal(false);
        // Reset selected team
        setSelectedTeam(null);
        // Refresh teams list
        fetchTeams();
      } else if (response.status === 401) {
        handleUnauthorized();
        return;
      } else {
        const data = await response.json();
        throw new Error(data.message || 'Failed to update team');
      }
    } catch (err) {
      setError(err.message || 'Failed to update team');
    } finally {
      // Ensure spinner state is fully reset
      setTimeout(() => {
        setEditingTeam(false);
      }, 100);
    }
  };

  // Archive team
  const handleArchiveTeam = async () => {
    // Skip isArchiveConfirmed check if team has no members
    if (removingTeam || !selectedTeam) return;
    
    // If the team has members and confirmation not provided, return
    if (selectedTeam.memberCount > 0 && !isArchiveConfirmed) return;

    setRemovingTeam(true);
    setError('');

    try {
      const token = getToken();
      if (!token) {
        handleUnauthorized();
        return;
      }

      const response = await fetch(`${API_BASE}/company/teams/${selectedTeam.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        // Close modal
        setShowArchiveTeamModal(false);
        // Reset selected team
        setSelectedTeam(null);
        // Reset confirmation
        setIsArchiveConfirmed(false);
        
        // Update local state immediately for UI responsiveness
        setTeams(prevTeams => prevTeams.filter(team => team.id !== selectedTeam.id));
        
        // Refresh all data from server to ensure consistency
        fetchTeams();
      } else if (response.status === 401) {
        handleUnauthorized();
        return;
      } else {
        const data = await response.json();
        throw new Error(data.message || 'Failed to archive team');
      }
    } catch (err) {
      setError(err.message || 'Failed to archive team');
    } finally {
      // Ensure spinner state is fully reset
      setTimeout(() => {
        setRemovingTeam(false);
      }, 100);
    }
  };

  // Add a new function to handle direct archiving without confirmation
  const handleDirectArchive = async (team) => {
    if (removingTeam) return;
    
    setRemovingTeam(true);
    setError('');

    try {
      const token = getToken();
      if (!token) {
        handleUnauthorized();
        return;
      }

      const response = await fetch(`${API_BASE}/company/teams/${team.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        // Update local state immediately for UI responsiveness
        setTeams(prevTeams => prevTeams.filter(t => t.id !== team.id));
        
        // Refresh all data from server to ensure consistency
        fetchTeams();
      } else if (response.status === 401) {
        handleUnauthorized();
        return;
      } else {
        const data = await response.json();
        throw new Error(data.message || 'Failed to archive team');
      }
    } catch (err) {
      setError(err.message || 'Failed to archive team');
    } finally {
      // Ensure spinner state is fully reset
      setTimeout(() => {
        setRemovingTeam(false);
      }, 100);
    }
  };

  // Fetch team members
  const fetchTeamMembers = async (teamId, teamName) => {
    try {
      const token = getToken();
      if (!token) {
        handleUnauthorized();
        return;
      }

      setError('');
      
      const response = await fetch(`${API_BASE}/company/users`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const allUsers = await response.json();
        
        // Filter users by team name
        const membersOfTeam = allUsers.filter(user => user.teamName === teamName);
        setTeamMembers(membersOfTeam);
      } else if (response.status === 401) {
        handleUnauthorized();
        return;
      } else {
        throw new Error('Failed to fetch team members');
      }
    } catch (err) {
      setError('Failed to fetch team members. Please try again.');
    }
  };

  // Handle viewing team members
  const handleViewTeamMembers = (team) => {
    setSelectedTeam(team);
    fetchTeamMembers(team.id, team.name);
    setShowTeamMembersModal(true);
  };

  // Add user to team
  const handleAddUserToTeam = async (userId, teamId = null) => {
    if (!teamId && !selectedTeam) return;
    
    const targetTeamId = teamId || selectedTeam.id;
    const targetTeamName = selectedTeam.name;
    
    setAddingUsersToTeam(true);
    setError('');
    
    try {
      const token = getToken();
      if (!token) {
        handleUnauthorized();
        return;
      }

      const response = await fetch(`${API_BASE}/company/users/${userId}/team`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          teamId: targetTeamId,
          teamName: targetTeamName
        })
      });

      if (response.ok) {
        // Refresh the team members list if we're in team view
        if (selectedTeam && selectedTeam.id === targetTeamId) {
          fetchTeamMembers(selectedTeam.id, selectedTeam.name);
        }
        
        // Close the unassigned users modal if it's open
        if (showUnassignedUsersModal) {
          setShowUnassignedUsersModal(false);
        }
        
        // Also refresh the teams and unassigned users
        fetchTeams().then(() => fetchUsers());
      } else if (response.status === 401) {
        handleUnauthorized();
        return;
      } else {
        const data = await response.json();
        throw new Error(data.message || 'Failed to add user to team');
      }
    } catch (err) {
      setError(err.message || 'Failed to add user to team');
    } finally {
      setAddingUsersToTeam(false);
    }
  };

  // Remove user from team
  const handleRemoveUserFromTeam = async (userId) => {
    if (!selectedTeam || removingUserFromTeam) return;
    
    setRemovingUserFromTeam(true);
    setError('');
    
    try {
      const token = getToken();
      if (!token) {
        handleUnauthorized();
        return;
      }

      const response = await fetch(`${API_BASE}/company/users/${userId}/team`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        // Refresh the team members list
        fetchTeamMembers(selectedTeam.id, selectedTeam.name);
        
        // Explicitly fetch users to update unassigned users list
        fetchUsers();
      } else if (response.status === 401) {
        handleUnauthorized();
        return;
      } else {
        const data = await response.json();
        throw new Error(data.message || 'Failed to remove user from team');
      }
    } catch (err) {
      setError(err.message || 'Failed to remove user from team');
    } finally {
      setRemovingUserFromTeam(false);
    }
  };

  // Filter unassigned users by search query
  const filteredUnassignedUsers = unassignedUsers.filter(user => 
    user.name?.toLowerCase().includes(searchQuery.toLowerCase()) || 
    user.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Filter team members by search query
  const filteredTeamMembers = teamMembers.filter(user => 
    user.name?.toLowerCase().includes(searchQuery.toLowerCase()) || 
    user.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Add state for collapsing team members section
  const [teamMembersCollapsed, setTeamMembersCollapsed] = useState(false);
  // Add state for tracking which user is being removed (for confirmation)
  const [userToRemove, setUserToRemove] = useState(null);

  // Add states for collapsible sections and contact modal
  const [unassignedUsersCollapsed, setUnassignedUsersCollapsed] = useState(true);
  const [showContactModal, setShowContactModal] = useState(false);
  // Add state for tracking which user is being removed (for confirmation)
  const [userForContact, setUserForContact] = useState(null);

  if (loading) {
    return <LoadingScreen />;
  }

  const processedTeams = sortData(teams);
  const hasTeams = processedTeams.length > 0;

  return (
    <div className="relative w-full h-screen overflow-y-auto bg-gradient-to-b from-[var(--bg-start)] to-[var(--bg-end)] custom-scrollbar">
      <style>
        {`
          /* Custom scrollbar styling */
          .custom-scrollbar::-webkit-scrollbar {
            width: 8px;
          }
          
          .custom-scrollbar::-webkit-scrollbar-track {
            background: var(--bg-start);
            border-radius: 10px;
          }
          
          .custom-scrollbar::-webkit-scrollbar-thumb {
            background: rgba(94, 53, 177, 0.2); /* Using the accent primary color with low opacity */
            border-radius: 10px;
            border: 2px solid var(--bg-start);
            transition: all 0.2s ease;
          }
          
          .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: rgba(94, 53, 177, 0.3);
          }
          
          @media (max-width: 640px) {
            .custom-scrollbar::-webkit-scrollbar {
              width: 4px;
            }
            
            .custom-scrollbar::-webkit-scrollbar-thumb {
              border: 1px solid var(--bg-start);
            }
          }
          
          /* Firefox support */
          .custom-scrollbar {
            scrollbar-width: thin;
            scrollbar-color: rgba(94, 53, 177, 0.2) transparent;
          }

          /* Make spinner animations consistent */
          @keyframes spinner {
            to {
              transform: rotate(360deg);
            }
          }
          
          .spinner {
            display: inline-block;
            width: 1.25rem;
            height: 1.25rem;
            border-radius: 50%;
            border: 2px solid white;
            border-top-color: transparent;
            animation: spinner 0.6s linear infinite;
          }

          /* Tooltip styles */
          .tooltip {
            position: absolute;
            z-index: 9999;
            pointer-events: none;
            transition: opacity 0.2s;
            white-space: nowrap;
            transform: translateY(-100%);
            top: -5px;
          }
          
          /* Additional z-index for rows to ensure proper stacking */
          .table-row {
            position: relative;
            z-index: 1;
          }
          
          /* No hover effect for table rows */
        `}
      </style>
      <div className="max-w-6xl mx-auto p-2 sm:p-3 pb-6 pl-4 sm:pl-5 md:pl-8">
        <div className="mb-6 sm:mb-8">
          <div className="flex justify-between items-center mb-3">
            <h1 className="text-xl sm:text-2xl font-bold text-[var(--text-primary)]">Manage Teams</h1>
            <button
              onClick={() => setShowAddTeamModal(true)}
              className="flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-[var(--accent-primary)] text-white rounded-md hover:bg-[var(--hover-dark)] transition-colors"
            >
              <Plus className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>Add Team</span>
            </button>
          </div>
          <div className="flex justify-end mb-3 hidden sm:flex">
            <button
              onClick={() => {
                // Force a complete refresh of teams and users
                fetchTeams();
              }}
              disabled={refreshing}
              className="flex items-center gap-2 px-3 py-1.5 text-[var(--text-secondary)] hover:bg-[var(--hover-light)] rounded-md transition-colors disabled:opacity-50"
              aria-label="Refresh teams list"
            >
              <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-3 bg-red-100 border border-red-400 text-red-700 rounded flex items-center gap-2">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-1">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            {error}
          </div>
        )}

        {success && (
          <div className="mb-6 p-3 bg-green-100 border border-green-400 text-green-700 rounded flex items-center gap-2 animate-[fadeIn_0.3s_ease-in-out]">
            <CheckCircle2 className="w-5 h-5 text-green-500" />
            {success}
          </div>
        )}

        <div className="bg-[var(--card-bg)] rounded-lg border border-[var(--border-color)] overflow-visible mt-4">
          {!hasTeams ? (
            <div className="p-6 text-center text-[var(--text-secondary)]">
              <div className="flex flex-col items-center justify-center py-10">
                <Users className="w-16 h-16 text-[var(--text-secondary)] opacity-30 mb-4" />
                <h3 className="text-lg font-medium text-[var(--text-primary)] mb-2">No Teams Available</h3>
                <p className="max-w-md mb-6">Teams allow you to organize users and operations. Create your first team to get started.</p>
                <button 
                  onClick={() => setShowAddTeamModal(true)}
                  className="px-4 py-2 bg-[var(--accent-primary)] text-white rounded-md hover:bg-[var(--hover-dark)] transition-colors flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add First Team
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Mobile View */}
              <div className="block sm:hidden">
                <div className="px-3 py-2 border-b border-[var(--border-color)] bg-[var(--card-bg)] bg-opacity-50">
                  <div className="flex items-center justify-between gap-2">
                    <button
                      onClick={() => {
                        // Force a complete refresh of teams and users
                        fetchTeams();
                      }}
                      disabled={refreshing}
                      className="p-1.5 text-[var(--text-secondary)] hover:bg-[var(--hover-light)] rounded-full transition-colors disabled:opacity-50"
                      aria-label="Refresh teams list"
                    >
                      <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
                    </button>
                    <button
                      onClick={() => setShowSortModal(true)}
                      className="p-1.5 text-[var(--text-secondary)] hover:bg-[var(--hover-light)] rounded-full transition-colors"
                      aria-label="Sort teams"
                    >
                      {sortConfig.direction === 'asc' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                {processedTeams.map(team => (
                  <div key={team.id} className="border-b border-[var(--border-color)] p-3" onClick={() => handleViewTeamMembers(team)}>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-medium text-[var(--text-primary)] brightness-110 text-base">
                            {team.name}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 text-[var(--text-secondary)] text-sm">
                          <Users className="w-4 h-4" />
                          <span>{team.memberCount !== undefined ? team.memberCount : 0} {team.memberCount === 1 ? 'member' : 'members'}</span>
                        </div>
                      </div>
                                                <div className="relative" style={{ position: 'static' }}>                            <button                              type="button"                              data-menu-button                              onClick={(e) => {                                e.stopPropagation();                                const rect = e.currentTarget.getBoundingClientRect();                                const position = calculateMenuPosition(rect);                                setButtonPosition(position);                                setActionMenuOpen(actionMenuOpen === team.id ? null : team.id);                              }}                              className="p-2 rounded-full text-[var(--text-secondary)] hover:bg-[var(--hover-light)] transition-colors"                            >                              <MoreVertical className="w-5 h-5" />                            </button>                                                        {actionMenuOpen === team.id && (                              <div                                 ref={actionMenuRef}                                data-menu-content                                className="fixed rounded-md shadow-lg bg-[var(--card-bg)] border border-[var(--border-color)] z-[99999] py-1 w-48"                                style={{                                  top: `${buttonPosition.top}px`,                                  left: `${buttonPosition.left}px`,                                  filter: 'drop-shadow(0 10px 8px rgb(0 0 0 / 0.04)) drop-shadow(0 4px 3px rgb(0 0 0 / 0.1))'                                }}                              >
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedTeam(team);
                                setShowEditTeamModal(true);
                                setActionMenuOpen(null);
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-[var(--text-primary)] hover:bg-[var(--hover-light)] transition-colors flex items-center gap-2"
                            >
                              <Edit className="w-4 h-4" />
                              Edit Team
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedTeam(team);
                                // Check if team has members
                                if (team.memberCount > 0) {
                                  // Show confirmation modal only if team has members
                                  setShowArchiveTeamModal(true);
                                } else {
                                  // If team has no members, archive immediately without confirmation
                                  handleDirectArchive(team);
                                }
                                setActionMenuOpen(null);
                              }}
                              className="w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-red-50/50 transition-colors flex items-center gap-2"
                            >
                              <Trash2 className="w-4 h-4" />
                              Archive Team
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop View */}
              <div className="hidden sm:block">
                <div className="relative overflow-hidden bg-[var(--card-bg)] rounded-lg border border-[var(--border-color)]">
                  {/* Grid Header */}
                  <div className="grid grid-cols-12 border-b border-[var(--border-color)] bg-[var(--card-bg)] sticky top-0 z-10 rounded-t-lg overflow-hidden w-full">
                    <div onClick={() => handleSort('name')} className="px-6 py-4 text-left col-span-7 cursor-pointer">
                      <div className="flex items-center gap-2 group">
                        <span className="text-sm font-medium text-[var(--text-secondary)]">Team Name</span>
                        <SortIcon columnKey="name" />
                      </div>
                    </div>
                    <div onClick={() => handleSort('memberCount')} className="px-6 py-4 text-left col-span-3 cursor-pointer">
                      <div className="flex items-center gap-2 group">
                        <span className="text-sm font-medium text-[var(--text-secondary)]">Members</span>
                        <SortIcon columnKey="memberCount" />
                      </div>
                    </div>
                    <div className="px-6 py-4 text-left col-span-2">
                      <div className="flex items-center gap-2 group">
                        <span className="text-sm font-medium text-[var(--text-secondary)]">Actions</span>
                      </div>
                    </div>
                  </div>

                  {/* Grid Rows */}
                  <div className="divide-y divide-[var(--border-color)]">
                    {processedTeams.map((team, index) => (
                      <div 
                        key={team.id} 
                        className={`grid grid-cols-12 bg-[var(--background)] bg-opacity-50 table-row
                          ${index === processedTeams.length - 1 ? 'rounded-b-lg overflow-hidden' : ''}`}
                        onClick={() => handleViewTeamMembers(team)}
                      >
                                                <div className="px-6 py-4 col-span-7 flex items-center">                          <div                             className="flex items-center gap-2 w-full py-1 px-2 rounded-md cursor-pointer"                            onClick={() => handleViewTeamMembers(team)}                          >                            <div className="flex-shrink-0 bg-blue-100 text-blue-600 p-1.5 rounded-md">                              <Hash className="w-4 h-4" />                            </div>                            <div className="flex items-center gap-1.5">                              <span className="text-[var(--text-primary)] font-medium brightness-110 text-sm">                                {team.name}                              </span>                              <div className="text-[var(--accent-primary)] text-xs font-medium bg-[var(--accent-primary)]/10 px-1.5 py-0.5 rounded">                                View                              </div>                            </div>                          </div>                        </div>
                        <div className="px-6 py-4 col-span-3 flex items-center">
                          <div className="flex items-center gap-2 text-[var(--text-secondary)]">
                            <Users className="w-4 h-4" />
                            <span className="text-sm">{team.memberCount !== undefined ? team.memberCount : 0}</span>
                          </div>
                        </div>
                        <div className="px-6 py-4 col-span-2 flex items-center justify-end">
                          <div className="flex items-center gap-2 relative" style={{ position: 'static' }}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const rect = e.currentTarget.getBoundingClientRect();
                                const position = calculateMenuPosition(rect);
                                setButtonPosition(position);
                                setActionMenuOpen(actionMenuOpen === team.id ? null : team.id);
                              }}
                              data-menu-button
                              className="p-1.5 text-[var(--text-primary)] hover:bg-[var(--hover-light)] rounded-full transition-colors z-20"
                            >
                              <MoreVertical className="w-5 h-5" strokeWidth={1.5} />
                            </button>
                            
                            {actionMenuOpen === team.id && (
                              <div 
                                ref={actionMenuRef}
                                data-menu-content
                                className="fixed rounded-md shadow-lg bg-[var(--card-bg)] border border-[var(--border-color)] z-[99999] py-1 w-48"
                                style={{
                                  top: `${buttonPosition.top}px`,
                                  left: `${buttonPosition.left}px`,
                                  filter: 'drop-shadow(0 10px 8px rgb(0 0 0 / 0.04)) drop-shadow(0 4px 3px rgb(0 0 0 / 0.1))'
                                }}
                              >
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedTeam(team);
                                    setShowEditTeamModal(true);
                                    setActionMenuOpen(null);
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-[var(--text-primary)] hover:bg-[var(--hover-light)] transition-colors flex items-center gap-2"
                                >
                                  <Edit className="w-4 h-4" />
                                  Edit Team
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedTeam(team);
                                    // Check if team has members
                                    if (team.memberCount > 0) {
                                      // Show confirmation modal only if team has members
                                      setShowArchiveTeamModal(true);
                                    } else {
                                      // If team has no members, archive immediately without confirmation
                                      handleDirectArchive(team);
                                    }
                                    setActionMenuOpen(null);
                                  }}
                                  className="w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-red-50/50 transition-colors flex items-center gap-2"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  Archive Team
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Bottom spacing to ensure all content is visible when scrolling */}
        <div className="h-6"></div>

        {/* Manage Unassigned Members Link */}      
        {unassignedUsers.length > 0 && (        
          <div className="max-w-6xl mx-auto p-2 sm:p-3">          
            <div className="mb-3">            
              <h2 className="text-lg sm:text-xl font-bold text-[var(--text-primary)]">Unassigned Members</h2>          
            </div>                    
            <div                           
              className="bg-[var(--card-bg)] rounded-lg border border-[var(--border-color)] overflow-visible p-4 sm:p-5 mt-4"
            >            
              <div className="flex items-center gap-4">              
                <div className="bg-yellow-100 p-3 rounded-full">                
                  <Users className="w-6 h-6 text-yellow-600" />              
                </div>              
                <div className="flex-1">                
                  <h3 className="text-xl font-semibold text-[var(--text-primary)]">                  
                    {unassignedUsers.length} {unassignedUsers.length === 1 ? 'Member' : 'Members'}                
                  </h3>                
                  <p className="text-[var(--text-secondary)]">                  
                    Not assigned to any team                
                  </p>              
                </div>            
              </div>          
            </div>        
          </div>      
        )}
      </div>

      {/* Modals will be added here */}
      
      {/* Add Team Modal */}
      {showAddTeamModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-[var(--card-bg)] p-6 rounded-lg max-w-md w-full mx-4 max-h-[90vh] flex flex-col">
            <h2 className="text-xl font-bold text-[var(--text-primary)] mb-4">Add New Team</h2>
            <form onSubmit={handleAddTeam} className="flex flex-col h-full">
              <div className="space-y-4 mb-4">
                <div>
                  <label htmlFor="teamName" className="block text-sm font-medium text-[var(--text-primary)] mb-1">
                    Team Name*
                  </label>
                  <input
                    type="text"
                    id="teamName"
                    className="w-full px-3 py-2 bg-[var(--background)] text-[var(--text-primary)] border border-[var(--border-color)] rounded-md placeholder-[var(--text-secondary)] focus:outline-none focus:ring-1 focus:ring-[var(--accent-primary)]"
                    
                    value={newTeam.name}
                    onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })}
                    required
                  />
                </div>
              </div>
              
              {/* Unassigned Users Section */}
              {unassignedUsers.length > 0 && (
                <div className="flex-1 overflow-y-auto mb-4">
                  <h3 className="text-md font-semibold text-[var(--text-primary)] mb-2">
                    Add Members ({unassignedUsers.length} unassigned)
                  </h3>
                  <div className="border border-[var(--border-color)] rounded-md bg-[var(--background)] max-h-60 overflow-y-auto">
                    <div className="p-2 space-y-2">
                      {unassignedUsers.map(user => (
                        <div
                          key={user.id}
                          className={`flex items-center justify-between px-3 py-2 rounded-md ${
                            selectedUsers.includes(user.id)
                              ? 'bg-[var(--accent-primary)]/10 dark:bg-[var(--accent-primary)]/20'
                              : 'hover:bg-[var(--hover-light)]'
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <UserAvatar user={user} />
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-[var(--text-primary)] truncate">{user.name || user.fullName || user.email}</p>
                              <p className="text-xs text-[var(--text-secondary)] truncate">{user.email}</p>
                            </div>
                          </div>
                          <div>
                            <label className="inline-flex items-center">
                              <input
                                type="checkbox"
                                className="w-4 h-4 rounded border-[var(--border-color)] text-[var(--accent-primary)] focus:ring-[var(--accent-primary)] bg-[var(--background)] cursor-pointer transition-colors"
                                checked={selectedUsers.includes(user.id)}
                                onChange={() => {
                                  setSelectedUsers(prev => 
                                    prev.includes(user.id)
                                      ? prev.filter(id => id !== user.id)
                                      : [...prev, user.id]
                                  );
                                }}
                              />
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-between items-center mt-auto">
                <div className="text-sm text-[var(--text-secondary)]">
                  {selectedUsers.length > 0 && (
                    <span>{selectedUsers.length} member{selectedUsers.length > 1 ? 's' : ''} selected</span>
                  )}
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddTeamModal(false);
                      setNewTeam({ name: '' });
                      setSelectedUsers([]);
                    }}
                    className="px-4 py-2 text-[var(--text-primary)] hover:bg-[var(--hover-light)] rounded-md transition-colors"
                    disabled={creatingTeam}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={creatingTeam}
                    className="flex items-center justify-center gap-2 px-6 py-2 bg-[var(--accent-primary)] text-white rounded-md hover:bg-[var(--hover-dark)] transition-colors disabled:opacity-70 min-w-[150px]"
                  >
                    {creatingTeam ? (
                      <>
                        <div className="spinner mr-2"></div>
                        <span>{selectedUsers.length > 0 ? 'Creating & Assigning...' : 'Creating Team...'}</span>
                      </>
                    ) : (
                      <>
                        <Plus className="w-4 h-4" />
                        <span>Create Team</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Team Modal */}
      {showEditTeamModal && selectedTeam && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-[var(--card-bg)] p-6 rounded-lg max-w-md w-full mx-4">
            <h2 className="text-xl font-bold text-[var(--text-primary)] mb-4">Edit Team</h2>
            <form onSubmit={handleEditTeam}>
              <div className="space-y-4">
                <div>
                  <label htmlFor="editTeamName" className="block text-sm font-medium text-[var(--text-primary)] mb-1">
                    Team Name*
                  </label>
                  <input
                    type="text"
                    id="editTeamName"
                    className="w-full px-3 py-2 bg-[var(--background)] text-[var(--text-primary)] border border-[var(--border-color)] rounded-md placeholder-[var(--text-secondary)] focus:outline-none focus:ring-1 focus:ring-[var(--accent-primary)]"
                    placeholder="Engineering"
                    defaultValue={selectedTeam.name}
                    value={editedTeam.name}
                    onChange={(e) => setEditedTeam({ ...editedTeam, name: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2 mt-8">
                <button
                  type="button"
                  onClick={() => setShowEditTeamModal(false)}
                  className="px-4 py-2 text-[var(--text-primary)] hover:bg-[var(--hover-light)] rounded-md transition-colors"
                  disabled={editingTeam}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={editingTeam}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-white transition-colors ${
                    editingTeam 
                      ? 'bg-[var(--accent-primary)] opacity-75' 
                      : 'bg-[var(--accent-primary)] hover:bg-[var(--hover-dark)]'
                  }`}
                >
                  {editingTeam ? (
                    <>
                      <div className="spinner mr-2"></div>
                      <span>Saving...</span>
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirmation Modal for Archive Team */}
      {showArchiveTeamModal && selectedTeam && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-[var(--card-bg)] p-6 rounded-lg max-w-md w-full mx-4">
            <h2 className="text-xl font-bold text-[var(--text-primary)] mb-4">Archive Team</h2>
            
            <p className="text-[var(--text-primary)] mb-2">
              Are you sure you want to archive the team <span className="font-semibold">{selectedTeam.name}</span>?
            </p>
            
            {selectedTeam.memberCount > 0 ? (
              <>
                <div className="p-3 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded mb-4">
                  <p className="flex items-start gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 flex-shrink-0 mt-0.5"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path><path d="M12 9v4"></path><path d="M12 17h.01"></path></svg>
                    <span>
                      This team has <strong>{selectedTeam.memberCount}</strong> {selectedTeam.memberCount === 1 ? 'member' : 'members'}. Archiving this team will remove the team assignment from these users.
                    </span>
                  </p>
                </div>
                
                <div className="flex items-start mb-4">
                  <input
                    type="checkbox"
                    id="confirmArchive"
                    className="w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    onChange={(e) => setIsArchiveConfirmed(e.target.checked)}
                    checked={isArchiveConfirmed}
                  />
                  <label htmlFor="confirmArchive" className="ml-2 text-sm text-[var(--text-secondary)]">
                    I understand and want to proceed with archiving this team
                  </label>
                </div>
              </>
            ) : (
              // For teams with 0 members, auto-confirm the action
              <p className="text-sm text-[var(--text-secondary)] mb-4">
                This team has no members. You can archive it without affecting any users.
              </p>
            )}

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setShowArchiveTeamModal(false);
                  setSelectedTeam(null);
                  setIsArchiveConfirmed(false);
                }}
                className="px-4 py-2 text-[var(--text-primary)] hover:bg-[var(--hover-light)] rounded-md transition-colors"
                disabled={removingTeam}
              >
                Cancel
              </button>
              <button
                onClick={handleArchiveTeam}
                disabled={removingTeam || (selectedTeam.memberCount > 0 && !isArchiveConfirmed)}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-white transition-colors ${
                  removingTeam || (selectedTeam.memberCount > 0 && !isArchiveConfirmed)
                    ? 'bg-red-500 opacity-50 cursor-not-allowed' 
                    : 'bg-red-500 hover:bg-red-600'
                }`}
              >
                {removingTeam ? (
                  <>
                    <div className="spinner mr-2"></div>
                    <span>Archiving...</span>
                  </>
                ) : (
                  'Archive Team'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sort Modal for Mobile */}
      {showSortModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
          <div className="bg-[var(--card-bg)] rounded-t-lg sm:rounded-lg w-full sm:max-w-xs mx-auto">
            <div className="p-4 border-b border-[var(--border-color)]">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-[var(--text-primary)]">Sort</h3>
                <button
                  onClick={() => {
                    setTempSortConfig(prev => ({
                      ...prev,
                      direction: prev.direction === 'asc' ? 'desc' : 'asc'
                    }));
                  }}
                  className="p-1.5 text-[var(--text-secondary)] hover:bg-[var(--hover-light)] rounded-full transition-colors"
                >
                  {tempSortConfig.direction === 'asc' ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div className="p-2">
              <button
                onClick={() => setTempSortConfig(prev => ({ ...prev, key: 'name' }))}
                className={`w-full text-left px-4 py-3 rounded-md hover:bg-[var(--hover-light)] transition-colors ${tempSortConfig.key === 'name' ? 'text-[var(--accent-primary)]' : 'text-[var(--text-primary)]'}`}
              >
                Team Name
              </button>
              <button
                onClick={() => setTempSortConfig(prev => ({ ...prev, key: 'memberCount' }))}
                className={`w-full text-left px-4 py-3 rounded-md hover:bg-[var(--hover-light)] transition-colors ${tempSortConfig.key === 'memberCount' ? 'text-[var(--accent-primary)]' : 'text-[var(--text-primary)]'}`}
              >
                Members
              </button>
            </div>
            <div className="p-4 border-t border-[var(--border-color)] flex justify-end gap-2">
              <button
                onClick={() => {
                  setShowSortModal(false);
                  setTempSortConfig(sortConfig); // Reset to current sort
                }}
                className="px-4 py-2 text-[var(--text-primary)] hover:bg-[var(--hover-light)] rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  setSortConfig(tempSortConfig);
                  setShowSortModal(false);
                }}
                className="px-4 py-2 bg-[var(--accent-primary)] text-white rounded-md hover:bg-[var(--hover-dark)] transition-colors"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Team Members Modal */}
      {showTeamMembersModal && selectedTeam && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-hidden">
          <div className="bg-[var(--card-bg)] border border-[var(--border-color)] rounded-lg shadow-xl flex flex-col max-h-[90vh] w-full max-w-[680px] mx-4">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-color)]">
              <div className="flex items-center gap-3">
                <div className="bg-blue-100 text-blue-600 p-2 rounded-md">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-6 h-6"><path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2Z"/><path d="M18 14h-8"/><path d="M15 18h-5"/><path d="M10 6h8"/><path d="M7 9h0"/><path d="M7 6h0"/></svg>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-[var(--text-primary)]">{selectedTeam.name}</h2>
                  <p className="text-sm text-[var(--text-secondary)]">Team Members: {teamMembers.length}</p>
                </div>
              </div>
              <button
                onClick={() => {
                  setShowTeamMembersModal(false);
                  setSelectedTeam(null);
                  setTeamMembers([]);
                  setSearchQuery('');
                  setUserToRemove(null);
                  setTeamMembersCollapsed(false);
                  setUnassignedUsersCollapsed(false);
                }}
                className="p-2 rounded-full hover:bg-[var(--hover-light)] text-[var(--text-secondary)]"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
              </button>
            </div>
            
            {/* Search */}
            <div className="px-6 py-3 border-b border-[var(--border-color)]">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 text-[var(--text-secondary)]"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
                </div>
                <input
                  type="text"
                  className="bg-[var(--background)] w-full pl-10 pr-4 py-2 rounded-md border border-[var(--border-color)] focus:outline-none focus:ring-1 focus:ring-[var(--accent-primary)] text-[var(--text-primary)]"
                  placeholder="Search by name or email"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto custom-scrollbar p-2">
              {/* Team Members */}
              <div className="mb-4">
                <div className="px-4 py-2">
                  <div className="flex items-center justify-between">
                    <div 
                      className="flex items-center gap-2 cursor-pointer" 
                      onClick={() => setTeamMembersCollapsed(!teamMembersCollapsed)}
                    >
                      <h3 className="text-md font-semibold text-[var(--text-primary)]">
                        Team Members ({teamMembers.length})
                      </h3>
                      <ChevronDown className={`w-5 h-5 text-[var(--text-secondary)] transition-transform ${teamMembersCollapsed ? 'rotate-180' : ''}`} />
                    </div>
                  </div>
                </div>
                
                {!teamMembersCollapsed && (
                  teamMembers.length === 0 ? (
                    <div className="text-center py-6 text-[var(--text-secondary)]">
                      <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>No members in this team yet</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {filteredTeamMembers.map(user => (
                        <div key={user.id} className="flex items-center justify-between px-4 py-3 rounded-md">
                          <div className="flex items-center gap-3 min-w-0 flex-1 overflow-hidden">
                            <UserAvatar user={user} />
                            <div className="min-w-0 flex-1 overflow-hidden">
                              <p className="text-sm font-medium text-[var(--text-primary)] truncate">{user.name || user.fullName || user.email}</p>
                              <p className="text-xs text-[var(--text-secondary)] truncate">{user.email}</p>
                            </div>
                          </div>
                          <div className="flex-shrink-0 flex items-center gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setUserForContact(user);
                                setShowContactModal(true);
                              }}
                              className="p-1.5 text-[var(--text-primary)] hover:bg-[var(--hover-light)] rounded-full transition-colors"
                            >
                              <Contact className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setUserToRemove(user);
                              }}
                              className="p-1.5 text-red-500 hover:bg-red-50 rounded-full transition-colors"
                            >
                              <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )
                )}
              </div>
              
              {/* Unassigned Users */}
              {unassignedUsers.length > 0 && (
                <div>
                  <div className="px-4 py-2">
                    <div className="flex items-center justify-between">
                      <div 
                        className="flex items-center gap-2 cursor-pointer" 
                        onClick={() => setUnassignedUsersCollapsed(!unassignedUsersCollapsed)}
                      >
                        <h3 className="text-md font-semibold text-[var(--text-primary)]">
                          Add from Unassigned ({unassignedUsers.length})
                        </h3>
                        <ChevronDown className={`w-5 h-5 text-[var(--text-secondary)] transition-transform ${unassignedUsersCollapsed ? 'rotate-180' : ''}`} />
                      </div>
                    </div>
                  </div>
                  
                  {!unassignedUsersCollapsed && (
                    <div className="space-y-2">
                      {filteredUnassignedUsers.map(user => (
                        <div key={user.id} className="flex items-center justify-between px-4 py-3 rounded-md">
                          <div className="flex items-center gap-3 min-w-0 flex-1 overflow-hidden">
                            <UserAvatar user={user} />
                            <div className="min-w-0 flex-1 overflow-hidden">
                              <p className="text-sm font-medium text-[var(--text-primary)] truncate">{user.name || user.fullName || "No name set"}</p>
                              <p className="text-xs text-[var(--text-secondary)] truncate">{user.email}</p>
                            </div>
                          </div>
                          <div className="flex-shrink-0">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAddUserToTeam(user.id);
                              }}
                              className="px-3 py-1 bg-blue-500 text-white text-xs rounded-full hover:bg-blue-600 transition-colors"
                            >
                              Add to Team
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className="px-6 py-4 border-t border-[var(--border-color)] flex justify-end">
              <button
                onClick={() => {
                  setShowTeamMembersModal(false);
                  setSelectedTeam(null);
                  setTeamMembers([]);
                  setSearchQuery('');
                  setUserToRemove(null);
                  setTeamMembersCollapsed(false);
                  setUnassignedUsersCollapsed(false);
                }}
                className="px-4 py-2 bg-[var(--accent-primary)] text-white rounded-md hover:bg-[var(--hover-dark)]"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Contact Modal */}
      {showContactModal && userForContact && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-[var(--card-bg)] p-6 rounded-lg max-w-md w-full mx-4">
            <div className="flex items-center gap-2 mb-6">
              <UserAvatar user={userForContact} />
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-bold text-[var(--text-primary)]">
                    {userForContact.fullName || userForContact.name || <span className="italic text-[var(--text-secondary)]">No name set</span>}
                  </h2>
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">Email</label>
                <div className="flex items-center gap-2 text-[var(--text-primary)]">
                  <Mail className="w-4 h-4 text-[var(--text-secondary)]" />
                  <span className="text-sm flex-1">{userForContact.email}</span>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(userForContact.email);
                    }}
                    className="p-1.5 text-[var(--text-secondary)] hover:bg-[var(--hover-light)] rounded-full transition-colors"
                    title="Copy email"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
                  </button>
                </div>
              </div>

              {userForContact.phoneCountry && userForContact.phone && (
                <div>
                  <label className="block text-sm font-medium text-[var(--text-secondary)] mb-1">Phone</label>
                  <div className="flex items-center gap-2 text-[var(--text-primary)]">
                    <Phone className="w-4 h-4 text-[var(--text-secondary)]" />
                    <span className="text-sm flex-1">+{userForContact.phoneCountry} {userForContact.phone}</span>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(`+${userForContact.phoneCountry} ${userForContact.phone}`);
                      }}
                      className="p-1.5 text-[var(--text-secondary)] hover:bg-[var(--hover-light)] rounded-full transition-colors"
                      title="Copy phone number"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="mt-10 flex justify-end">
              <button
                onClick={() => {
                  setShowContactModal(false);
                  setUserForContact(null);
                }}
                className="px-4 py-2 bg-[var(--accent-primary)] text-white rounded-md hover:bg-[var(--hover-dark)] transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove User Confirmation Modal */}
      {userToRemove && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-[var(--card-bg)] p-6 rounded-lg max-w-md w-full mx-4">
            <div className="flex items-start gap-4 mb-6">
              <div className="p-2 rounded-full bg-amber-100 dark:bg-amber-800/30 text-amber-600">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-medium text-[var(--text-primary)] mb-2">
                  Change Team Assignment
                </h2>
                <p className="text-[var(--text-secondary)]">
                  This will move <span className="font-semibold text-[var(--text-primary)]">{userToRemove.name || userToRemove.fullName || userToRemove.email}</span> to the unassigned users section. You can reassign them to any team later.
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setUserToRemove(null)}
                className="px-4 py-2 border border-[var(--border-color)] rounded-md text-[var(--text-primary)] hover:bg-[var(--hover-light)] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  handleRemoveUserFromTeam(userToRemove.id);
                  setUserToRemove(null);
                }}
                className="px-4 py-2 bg-[var(--accent-primary)] hover:bg-[var(--hover-dark)] text-white rounded-md transition-colors"
              >
                Move to Unassigned
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
